/**
 * feature-config flag names owned by ai-design-assist
 *
 * please consider removing this file and adding it to an appropriate place in the
 * feature code (e.g. an entrypoint, or a package owned by the feature team), or
 * passing flag names directly as a string!
 *
 * by separating it out, we decouple the feature code from the feature config packages
 * and avoid the need to update this package when flags are added or removed.
 */

export const AI_DESIGN_ASSIST_FLAG_NAMES = {
  // The WFS Sync optimization gate. When on, serves eligible pulls from
  // persisted monolith data instead of one MPS site-data read per page, AND
  // projects the site-global lanes once per pull instead of once per page
  // (EXT-2658). Also the kill switch for both: off restores the per-page MPS
  // read and the per-page projection on the next request. Off by default.
  WFS_PERSISTED_READS: 'ff-ext-2749-wfs-persisted-reads',
  // Serves the normal MPS response, then recomputes the same pull under the
  // fully optimized configuration (persisted source + collapsed site-global
  // projection) and compares per-file hashes. Independent of the serve flag.
  // Off by default.
  WFS_PERSISTED_READS_SHADOW: 'ff-ext-2749-wfs-persisted-reads-shadow',
  AI_ASSISTANT: 'ff-grow-4750-ai-assistant',
  AI_ASSISTANT_ACTIVITY_LOGS_BUTTON: 'ff-grow-5157-aia-activity-logs-button',
  AI_ASSISTANT_DATADOG_LOGS_BUTTON: 'ff-ext-1002-aia-datadog-logs-button',
  AI_ASSISTANT_MODEL_PICKER: 'ff-grow-5044-aia-model-picker',
  AI_ASSISTANT_STYLE_EXTRACTION: 'ff-grow-5262-style-extraction',
  AI_DESIGN_ASSIST_DEBUG_TOOLS: 'ff-grow-4252-aisg-webflow-library-scripts',
  AI_ENTRYPOINT_AISB: 'ff-grow-5027-ai-entrypoint-aisb',
  AI_SITE_BUILDER_KILLSWITCH: 'ff-grow-4397-aisb-killswitch',
  AI_SITE_BUILDER_WORKERS_CIRCUIT_BREAKER:
    'ff-ai-site-builder-workers-circuit-breaker',
  DISABLE_LLM_CLIENT_MODERATION: 'ff-grow-5156-disabling-llm-client-moderation',
  ANTHROPIC_BATCH_SUBMISSIONS: 'ff-wfa-2864-anthropic-batch-submissions',
  LLM_MODERATION_CACHE: 'ff-aio-245-moderation-cache',
  GROWTH_AI_HELP: 'ff-grow-3209-growth-ai-help-jun-v1',
  OPENAI_PRIORITY_PROCESSING: 'ff-grow-5204-openai-priority-processing',
  SECTION_FRAGMENTS: 'ff-grow-4795-section-fragments',
  DOCKED_MODAL: 'ff-grow-5249-docked-modal',
  AIA_PANEL_RELOCATION: 'ff-apai-3017-aia-panel-relocation',
  AIA_DOCK_RESIZE: 'ff-ext-162-aia-dock-resize',
  AISB_LIBRARY_VERSION_MANIFEST: 'ff-cam-355-aisb-library-version-manifest',
  FILESYSTEM_AGENT: 'ff-aio-154-filesystem-agent',
  COMBINED_AIA_FILESYSTEM: 'ff-ext-851-combined-aia-filesystem',
  AIA_CLEAN_IDENTITY_RECONCILIATION:
    'ff-ext-2021-aia-clean-identity-reconciliation',
  // Per-site ramp layered under REALTIME_AGENT_PRESENCE; both must pass.
  AIA_AGENT_PRESENCE: 'ff-ext-2373-ai-assistant-agent-presence',
  AIA_TOOL_RESULT_COMPACTION: 'ff-aia-394-tool-result-compaction',
  AIA_TOOL_RESULT_SUPERSESSION: 'ff-ext-1049-tool-result-supersession',
  // Kill-switch for the provider-anchored summarization gate (EXT-1014). OFF
  // reverts the projection to the chars/4 estimate over the whole history,
  // which is the behaviour that under-counted and never fired.
  AIA_PROVIDER_ANCHORED_SUMMARIZATION:
    'ff-ext-1014-provider-anchored-summarization',
  AIA_PARALLEL_TOOLCALLS: 'ff-aia-443-parallel-toolcalls',
  // Kill-switch + per-workspace rollout for the Slack agent's token-threshold
  // summarization (ep-ai-bridge). OFF reverts to message-count summarization
  // ({enabled: true}), the pre-fix prod behavior. Gated in agentBuilder. DEVREL-3008.
  SLACK_AGENT_CONTEXT_COMPACTION: 'ff-devrel-3008-slack-context-compaction',
  // Kill-switch + per-user (userID) rollout for Claude Sonnet 5 on the Slack and
  // site-resolver agents (ep-ai-bridge). OFF reverts both to Sonnet 4.5. The two
  // agents share one gate because the resolver is a pre-stage of the Slack agent
  // — a partial rollout would put two models inside a single user turn. Gated in
  // agentBuilder. EXT-2185.
  SLACK_AGENTS_SONNET_5: 'ff-ext-2185-slack-agents-sonnet-5',
  // Kill-switch for the agent framework's stable error codes (context_limit,
  // step_limit_exceeded). OFF restores the pre-fix events: the generic
  // "Agent execution was aborted" abort and the raw provider/LangGraph error.
  // Safe to flip back off after events carrying the new reasons are already
  // persisted — `reason` is optional and no consumer switches exhaustively on
  // it. Gated in agent.ts and graph/nodes/model.ts. DEVREL-3008.
  AGENT_STABLE_ERROR_CODES: 'ff-devrel-3008-agent-stable-error-codes',
  TRIGGER_AIA_POST_AISB: 'ff-aia-448-trigger-aia-post-aisb',
  AIA_STYLE_CLEANUP_ARTIFACTS: 'ff-sps-5253-unused-style-cleanup',
  // ON restores legacy HTML reads; OFF or a missing gate keeps bounded reads.
  AIA_BOUNDED_HTML_READS_KILLSWITCH:
    'ff-ext-3057-bounded-html-reads-killswitch',
  AIA_TOOL_PERMISSION_ENFORCEMENT: 'ff-aia-401-tool-permission-enforcement',
  AIA_CREDITS_DISABLED: 'ff-aia-584-credits-disabled',
  CAMPAIGN_CREDITS_DISABLED: 'ff-campaign-credits-disabled',
  SLACK_CREDITS_DISABLED: 'ff-slack-ai-credits-disabled',
  // EXT-2243 decision 10 (inverted, fails closed): on = Slack agent turns keep
  // the MCP tools that ask for a confirmation; off or unreadable = the MCP
  // server withholds them.
  SLACK_KEEP_CONFIRM_TOOLS: 'ff-ext-2243-slack-keep-confirm-tools',
  // Incremental-rollout gate for the consolidated public-mcp component/element
  // tools exposed to the AI assistant (layered on top of
  // CODE_GEN_FLAG_NAMES.PAGE_AUTOMATION, which gates the underlying
  // page-automation surface). Create the matching Statsig gate before enabling.
  AIA_PUBLIC_MCP_COMPONENT_TOOLS: 'ff-ext-602-aia-public-mcp-component-tools',
  // Route AICC visual-changes turns (drafted style/text edits) to a cheaper code
  // model to keep them within ~1 AI credit. Off → those turns use the default model.
  AICC_VISUAL_CHANGES_CHEAP_MODEL: 'ff-code-2870-visual-changes-cheap-model',
  AICC_VISUAL_CHANGES_ZERO_CREDITS: 'ff-code-2870-visual-changes-zero-credits',
  // Collapse large chat pastes into an editable composer chip (EXT-303) so the
  // blob stays out of the textarea/history and off the message char limit.
  LARGE_PASTE_CHIP: 'ff-ext-303-large-paste-chip',
};

export const AI_DESIGN_ASSIST_EXPERIMENT_NAMES = {
  AI_SITE_BUILDER: 'exp-grow-4435-ai-site-builder',
  AI_SITE_BUILDER_GPT5_NANO_VS_MINI: 'exp-grow-5349-aisb-gpt5-nano-vs-mini',
} as const;
